import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Badge } from "./ui/badge";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from "recharts";
import { Building2, Leaf, Zap, Droplets, Trash2, ChevronDown, ChevronRight, Layers } from "lucide-react";
import { toast } from "sonner";

export function FacilityBreakdown({ year }) {
  const [data, setData] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [view, setView] = useState("site");

  useEffect(() => {
    const params = year ? { year } : {};
    api.get("/facilities/dashboard", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [year]);

  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading facility breakdown…</div>;
  const fac = data.facilities || [];
  if (!fac.length) {
    return <div className="card-flat p-10 text-center text-muted-foreground" data-testid="facility-breakdown-empty">
      No facility data yet — assign facilities to records in Data Management, or add sites in Settings → Facilities.
    </div>;
  }
  const active = fac.filter((f) => (f.records || 0) > 0 || f.unassigned);
  const inactive = fac.filter((f) => (f.records || 0) === 0 && !f.unassigned);
  const chart = active.filter((f) => f.emissions_t > 0 || f.energy_mwh > 0).slice(0, 12);

  const byGroup = Object.values(active.reduce((m, f) => {
    const g = f.unassigned ? "Unassigned" : (f.group || "Ungrouped");
    const e = (m[g] ||= { name: g, emissions_t: 0, energy_mwh: 0, water_m3: 0, waste_t: 0, records: 0, sites: 0 });
    e.emissions_t += f.emissions_t || 0; e.energy_mwh += f.energy_mwh || 0;
    e.water_m3 += f.water_m3 || 0; e.waste_t += f.waste_t || 0;
    e.records += f.records || 0; e.sites += 1;
    return m;
  }, {})).sort((a, b) => b.emissions_t - a.emissions_t);
  const groupChart = byGroup.filter((g) => g.emissions_t > 0 || g.energy_mwh > 0);
  const chartData = view === "group" ? groupChart : chart;

  const FacilityCard = ({ f }) => (
    <div key={f.name} className={`card-flat p-4 ${f.unassigned ? "border-[#FFB800]/40" : ""}`} data-testid={`facility-card-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm truncate flex items-center gap-1.5">
            <Building2 className={`h-3.5 w-3.5 shrink-0 ${f.unassigned ? "text-[#FFB800]" : "text-accent"}`} /> {f.name}
          </div>
          {f.location && <div className="text-[10px] text-muted-foreground">{f.location}</div>}
        </div>
        {f.unassigned
          ? <Badge variant="warning" className="text-[8px] uppercase shrink-0">gap</Badge>
          : !f.is_master && <Badge variant="outline" className="text-[8px] shrink-0" title="Facility label on records but not in the Settings master list">unlisted</Badge>}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Leaf className="h-3 w-3" /> Emissions</div><div className="font-mono font-bold">{formatNum(f.emissions_t, { decimals: 1 })} <span className="font-sans text-muted-foreground text-[10px]">tCO₂e</span></div></div>
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Zap className="h-3 w-3" /> Energy</div><div className="font-mono font-bold">{formatNum(f.energy_mwh, { decimals: 1 })} <span className="font-sans text-muted-foreground text-[10px]">MWh</span></div></div>
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Droplets className="h-3 w-3" /> Water</div><div className="font-mono font-bold">{formatNum(f.water_m3, { decimals: 0 })} <span className="font-sans text-muted-foreground text-[10px]">m³</span></div></div>
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Trash2 className="h-3 w-3" /> Waste</div><div className="font-mono font-bold">{formatNum(f.waste_t, { decimals: 1 })} <span className="font-sans text-muted-foreground text-[10px]">t</span></div></div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 border-t border-border pt-1.5">{f.records} record(s)</div>
    </div>
  );

  const GroupCard = ({ g }) => (
    <div className={`card-flat p-4 ${g.name === "Unassigned" ? "border-[#FFB800]/40" : ""}`} data-testid={`group-card-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="font-display font-semibold text-sm truncate flex items-center gap-1.5">
            <Layers className={`h-3.5 w-3.5 shrink-0 ${g.name === "Unassigned" ? "text-[#FFB800]" : "text-accent"}`} /> {g.name}
          </div>
          <div className="text-[10px] text-muted-foreground">{g.sites} site{g.sites > 1 ? "s" : ""}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Leaf className="h-3 w-3" /> Emissions</div><div className="font-mono font-bold">{formatNum(g.emissions_t, { decimals: 1 })} <span className="font-sans text-muted-foreground text-[10px]">tCO₂e</span></div></div>
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Zap className="h-3 w-3" /> Energy</div><div className="font-mono font-bold">{formatNum(g.energy_mwh, { decimals: 1 })} <span className="font-sans text-muted-foreground text-[10px]">MWh</span></div></div>
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Droplets className="h-3 w-3" /> Water</div><div className="font-mono font-bold">{formatNum(g.water_m3, { decimals: 0 })} <span className="font-sans text-muted-foreground text-[10px]">m³</span></div></div>
        <div><div className="text-muted-foreground text-[9px] uppercase tracking-wider flex items-center gap-1"><Trash2 className="h-3 w-3" /> Waste</div><div className="font-mono font-bold">{formatNum(g.waste_t, { decimals: 1 })} <span className="font-sans text-muted-foreground text-[10px]">t</span></div></div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 border-t border-border pt-1.5">{g.records} record(s)</div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="facility-breakdown">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="facility-view-toggle">
          {[["site", "By site"], ["group", "By group"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`facility-view-${v}`}
              className={`px-3 py-1.5 text-[11px] font-mono transition-colors ${view === v
                ? "bg-accent text-accent-foreground font-bold"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid={view === "group" ? "facility-group-grid" : "facility-site-grid"}>
        {view === "group" ? byGroup.map((g) => <GroupCard key={g.name} g={g} />) : active.map((f) => <FacilityCard key={f.name} f={f} />)}
      </div>

      {view === "site" && inactive.length > 0 && (
        <div data-testid="facility-inactive-section">
          <button
            type="button"
            onClick={() => setShowInactive((v) => !v)}
            data-testid="facility-toggle-inactive"
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showInactive ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Inactive / not yet onboarded ({inactive.length})
          </button>
          {showInactive && (
            <>
              <p className="text-[10px] text-muted-foreground mt-1">These sites have no records yet — excluded from charts and intensity maths until data is assigned.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mt-2">
                {inactive.map((f) => <FacilityCard key={f.name} f={f} />)}
              </div>
            </>
          )}
        </div>
      )}

      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card-flat p-4" data-testid="facility-emissions-chart">
            <div className="label-eyebrow mb-2">Emissions by {view === "group" ? "group" : "facility"} (tCO₂e)</div>
            <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 34)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#aaa" }} />
                <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} formatter={(v) => [`${formatNum(v, { decimals: 1 })} tCO₂e`, "Emissions"]} />
                <Bar dataKey="emissions_t" fill="#00E65B" radius={[0, 3, 3, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="card-flat p-4" data-testid="facility-energy-chart">
            <div className="label-eyebrow mb-2">Energy by {view === "group" ? "group" : "facility"} (MWh)</div>
            <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 34)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#aaa" }} />
                <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }} formatter={(v) => [`${formatNum(v, { decimals: 1 })} MWh`, "Energy"]} />
                <Bar dataKey="energy_mwh" fill="#60A5FA" radius={[0, 3, 3, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
